import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useLocalStorage } from "@vueuse/core";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import type { AddPortfolioResponse, GraphQlPayload, PersistedQueryPayload } from "@/types/api/fetch";
import { goFetch, isSuccessState } from "@/utils/network-utils";
import type { MyPortfolio, MyPortfolios, MyPosition } from "@/types/api/my-portfolios";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

// State values
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );

const defaultPortfolios: MyPortfolio[] = [];
const allPortfolios = useLocalStorage< MyPortfolio[] >( 'my-portfolios', defaultPortfolios );
const selectedPortfolioId = useLocalStorage< number | null >( 'selected-portfolio', null );
const selectedPortfolio = computed< MyPortfolio | undefined >( () => {
    let portfolio = allPortfolios.value.length > 0 ? allPortfolios.value[ 0 ] : undefined;
    if ( !!( selectedPortfolioId.value ) ) {
        portfolio = allPortfolios.value.find( ( portfolio: MyPortfolio ) => portfolio.portfolio_id === selectedPortfolioId.value );
    }

    selectedPortfolioId.value = portfolio?.portfolio_id;

    return portfolio;
} );

export const useMyPortfoliosStore = defineStore( 'my-portfolios', () => {
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();

    function reset(): void {
        // Clear out the local storage value
        allPortfolios.value = null;
        selectedPortfolioId.value = null;
        loading.value = false;

        // Reset the local storage variable to the "uninitialized" value
        allPortfolios.value = defaultPortfolios;
    }

    async function initialize(): Promise< void > {
        // If not authenticated or the application context has not yet been created, return immediately
        if ( !isAuthenticated.value ) {
            logger.debug( '[ MyPortfolio ].initialize() exiting - not ready yet...: [ not authenticated => ', !isAuthenticated.value, ' ]' );
            return;
        }

        if ( loading.value ) {
            return;
        }

        loading.value = true;

        try {
            const result = await goFetch< MyPortfolios, PersistedQueryPayload >( {
                endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
                payload: {
                    id: '024e15d4840a5f62f988928cb8450caa',
                    variables: { skip: 0, limit: 6 }
                },
                authToken: authToken.value
            } );

            // If API call failed (returned null), don't reset - just skip processing
            // Don't return early - let finally block run to clear loading flag
            if (!result) {
                logger.warn('My portfolios API call failed, will not retry immediately');
            } else if ( result?.data ) {
                if ( result.data?.myPortfolios?.portfolios?.length > 0 ) {
                    allPortfolios.value = [ ...result?.data?.myPortfolios?.portfolios ?? [] ];
                } else {
                    try {
                        // If the user does not have any portfolios, create a "default" portfolio and send it to the server
                        const defaultPortfolioName = `My First Portfolio`;
                        const response =
                            await goFetch< AddPortfolioResponse, GraphQlPayload >( {
                                endpoint: `user/graphql`,
                                authToken: authToken.value,
                                payload: {
                                    query: `mutation AddPortfolio($input: AddPortfolioInput!) { addPortfolio(input: $input) { id } }`,
                                    variables: {
                                        input: {
                                            userId: userStore.userId(),
                                            name: defaultPortfolioName
                                        }
                                    }
                                }
                            } );

                        // Update the local representation only if there are no errors and the id was returned as confirmation
                        if ( response?.data?.addPortfolio?.id && isSuccessState( response?.status ) ) {
                            const newPortfolioId = response.data.addPortfolio.id;
                            addNewPortfolio( {
                                portfolio_id: newPortfolioId,
                                portfolio_name: defaultPortfolioName,
                                positions: []
                            } );
                        }
                    } catch( err: any ) {
                        logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                    }
                }
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    function setSelectedPortfolio( portfolioId: number ) {
        selectedPortfolioId.value = portfolioId;
    }

    function setSelectedPortfolioTitle( title: string ) {
        if ( selectedPortfolio.value ) {
            selectedPortfolio.value!.portfolio_name = title;
        }
    }

    function addNewPortfolio( portfolio: MyPortfolio ) {
        // Add the new portfolio to the in-memory collection
        allPortfolios.value.push( portfolio );
        // Select the newly added portfolio
        selectedPortfolioId.value = portfolio.portfolio_id;
    }

    async function refreshPortfolios() {
        // Capture the currently selected portfolio
        const portfolioId = selectedPortfolioId.value;
        // Clear all state for the current set of portfolios
        reset();
        // Re-load all data from the server
        await initialize();
        // Re-set the selected portfolio
        if ( !!portfolioId ) {
            setSelectedPortfolio( portfolioId );
        }
    }

    function removeSelectedPortfolio() {
        const portfolioId = selectedPortfolioId.value;
        if ( portfolioId && allPortfolios.value.length > 1 ) {
            const index = allPortfolios.value.findIndex( p => p.portfolio_id === portfolioId );
            allPortfolios.value.splice( index, 1 );
            selectedPortfolioId.value = allPortfolios.value[ 0 ].portfolio_id;
        }
    }

    function removeSelectedPositionFromPortfolio( position: MyPosition ) {
        const positions = selectedPortfolio.value?.positions;
        if ( positions ) {
            const index = positions.findIndex( p => p.pos_id === position.pos_id );
            positions.splice( index, 1 );
        }
    }

    return {
        // ACTIONS
        initialize,
        addNewPortfolio,
        refreshPortfolios,
        removeSelectedPortfolio,
        removeSelectedPositionFromPortfolio,
        reset,
        setSelectedPortfolio,
        setSelectedPortfolioTitle,

        // GETTERS
        allPortfolios,
        isLoading,
        selectedPortfolio
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useMyPortfoliosStore, import.meta.hot ) );
}